import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

export const MainContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Dark variant is transparent so the site-wide blue gradient on
     body::before shows through. */
  background: ${({ theme }) => (theme === "light" ? "#f8f9fa" : "transparent")};
  padding: 20px;
  position: relative;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => 
      theme === "light" 
        ? "radial-gradient(ellipse at center, rgba(67, 233, 123, 0.1) 0%, transparent 70%)"
        : "radial-gradient(ellipse at center, rgba(67, 233, 123, 0.05) 0%, transparent 70%)"
    };
    pointer-events: none;
  }
`;

export const ContentCard = styled.div`
  background: ${({ theme }) => (theme === "light" ? "#ffffff" : "#152a47")};
  border-radius: 20px;
  padding: 60px 40px;
  box-shadow: ${({ theme }) => 
    theme === "light" 
      ? "0 25px 50px rgba(0, 0, 0, 0.1)" 
      : "0 25px 50px rgba(0, 0, 0, 0.3)"
  };
  border: ${({ theme }) =>
    theme === "light" ? "1px solid rgba(0, 0, 0, 0.05)" : "1px solid rgba(255, 255, 255, 0.1)"};
  max-width: 500px;
  width: 100%;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  z-index: 1;
  
  @media (max-width: 480px) {
    padding: 40px 30px;
    margin: 20px;
  }
`;

export const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ $success, theme }) => 
    $success 
      ? "linear-gradient(135deg, #43e97b, #38f9d7)"
      : "linear-gradient(135deg, #ff4757, #ff3742)"
  };
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px auto;
  animation: ${pulse} 2s ease-in-out infinite;
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

export const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
  color: ${({ theme }) => (theme === "light" ? "#1a1a1a" : "#ffffff")};
  line-height: 1.2;
  
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

export const Text = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => 
    theme === "light" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.8)"
  };
  margin-bottom: 32px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 24px;
`;

export const ActionButton = styled.button`
  background: linear-gradient(135deg, #43e97b, #38f9d7);
  color: #152a47;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(67, 233, 123, 0.4);
    background: linear-gradient(135deg, #38f9d7, #43e97b);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SecondaryButton = styled.button`
  background: ${({ theme }) => 
    theme === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)"
  };
  color: ${({ theme }) => (theme === "light" ? "#1a1a1a" : "#ffffff")};
  border: 2px solid ${({ theme }) => 
    theme === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)"
  };
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  
  &:hover {
    transform: translateY(-2px);
    background: ${({ theme }) => 
      theme === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.15)"
    };
    box-shadow: ${({ theme }) => 
      theme === "light" 
        ? "0 8px 25px rgba(0, 0, 0, 0.1)" 
        : "0 8px 25px rgba(255, 255, 255, 0.1)"
    };
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

export const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${({ theme }) => 
    theme === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"
  };
  border-top: 4px solid #43e97b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const LoadingText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => 
    theme === "light" ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.7)"
  };
  margin: 0;
`;
